import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react'

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: ReactNode
  hint?: ReactNode
  error?: string | null
}

/**
 * `Field`, for text that runs to more than one line.
 *
 * Deliberately carries **no `maxLength`** by default and none is passed by the
 * journal. The wire sets no length limit on `notes`, and a cap here would
 * silently truncate at the ceiling -- which is exactly the permanent,
 * unreconstructable loss the self-report contract is built to prevent. A limit,
 * if one is ever wanted, belongs to the backend and should surface as a visible
 * counter and a blocking error rather than as vanishing characters.
 */
export function TextArea({ label, hint, error, className, ...rest }: TextAreaProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        {...rest}
        id={id}
        className="field__control field__control--multiline"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  )
}
